import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Typography,
    IconButton,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Divider,
    useTheme,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { CancelButton } from 'src/components/CancelButton'
import { AppRoutesNavigation } from 'src/app-routes'
import { useCustomerCtx } from 'src/hooks/context-hooks'
import { toast } from 'react-toastify'
import { ChecklistAnswerType, CreateChecklistItemDto} from '../../../generated-types/checklist-types'
import { useCreateChecklist, useUpdateChecklist, useGetChecklistById } from 'src/api/useChecklist'

interface ItemState {
    label: string
    answerType: ChecklistAnswerType
    options: string[]
    newOption: string
}

const emptyItem = (): ItemState => ({
    label: '',
    answerType: ChecklistAnswerType.Boolean,
    options: [],
    newOption: '',
})

const ChecklistForm = () => {
    const { id: customerId, checklistId } = useParams<{ id: string; checklistId: string }>()
    const navigate = useNavigate()
    const { t } = useTranslation(['checklist', 'common'])
    const { customer } = useCustomerCtx()
    const theme = useTheme()
    const isEdit = !!checklistId

    const customerIdNum = customer?.id as number
    const checklistIdNum = checklistId ? parseInt(checklistId) : 0

    const { data: existingChecklist } = useGetChecklistById(customerIdNum, checklistIdNum)
    const createMutation = useCreateChecklist(customerIdNum)
    const updateMutation = useUpdateChecklist(customerIdNum, checklistIdNum)

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [items, setItems] = useState<ItemState[]>([emptyItem()])

    useEffect(() => {
        if (existingChecklist && isEdit) {
            setName(existingChecklist.name)
            setDescription(existingChecklist.description ?? '')
            setItems(
                existingChecklist.items.map(i => {
                    let options: string[] = []
                    if (Array.isArray(i.options)) {
                        options = i.options
                    } else if (typeof i.options === 'string') {
                        try { options = JSON.parse(i.options) } catch { options = [] }
                    }
                    return {
                        label: i.label,
                        answerType: i.answerType,
                        options,
                        newOption: '',
                    }
                }),
            )
        }
    }, [existingChecklist, isEdit])

    const updateItem = (index: number, patch: Partial<ItemState>) => {
        setItems(prev => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)))
    }

    const addItem = () => {
        setItems(prev => [...prev, emptyItem()])
    }

    const removeItem = (index: number) => {
        setItems(prev => prev.filter((_, i) => i !== index))
    }

    const addOption = (index: number) => {
        const item = items[index]
        if (!item.newOption.trim()) return
        updateItem(index, {
            options: [...item.options, item.newOption.trim()],
            newOption: '',
        })
    }

    const removeOption = (itemIndex: number, optionIndex: number) => {
        setItems(prev =>
            prev.map((item, i) =>
                i === itemIndex ? { ...item, options: item.options.filter((_, oi) => oi !== optionIndex) } : item,
            ),
        )
    }

    const answerTypeLabel = (type: ChecklistAnswerType) => {
        switch (type) {
            case ChecklistAnswerType.Boolean:
                return t('answer-type-boolean')
            case ChecklistAnswerType.Text:
                return t('answer-type-text')
            case ChecklistAnswerType.Radio:
                return t('answer-type-radio')
        }
    }

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error(t('name-required'))
            return
        }
        if (items.length === 0) {
            toast.error(t('items-required'))
            return
        }
        for (const item of items) {
            if (!item.label.trim()) {
                toast.error(t('item-label-required'))
                return
            }
            if (item.answerType === ChecklistAnswerType.Radio && item.options.length < 2) {
                toast.error(t('radio-options-required'))
                return
            }
        }

        const dto = {
            name: name.trim(),
            description: description.trim() || undefined,
            items: items.map(
                (item, index): CreateChecklistItemDto => ({
                    label: item.label.trim(),
                    answerType: item.answerType,
                    options: item.answerType === ChecklistAnswerType.Radio ? item.options : null,
                    order: index,
                }),
            ),
        }

        try {
            if (isEdit) {
                await updateMutation.mutateAsync(dto, {
                    onSuccess: () => {
                        toast.success(t('checklist-updated'))
                        navigate(AppRoutesNavigation.Customer.checklists(customerIdNum))
                    },
                })
            } else {
                await createMutation.mutateAsync(dto, {
                    onSuccess: () => {
                        toast.success(t('checklist-created'))
                        navigate(AppRoutesNavigation.Customer.checklists(customerIdNum))
                    },
                })
            }
        } catch {
            toast.error(t('error-saving-checklist'))
        }
    }

    const isSubmitting = createMutation.isLoading || updateMutation.isLoading

    return (
        <CommonPageLayout
            titleSection={{
                title: isEdit
                    ? t('edit-checklist')
                    : t('create-checklist'),
            }}
        >
            <Box p={3} bgcolor={theme.palette.background.paper} borderRadius={1}>
                <Box display="flex" flexDirection="column" gap={3}>

                    <TextField
                        label={t('name', { ns: 'common' }) + ' *'}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        fullWidth
                    />

                    <TextField
                        label={t('description', { ns: 'common' })}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    <Divider />

                    <Typography variant="h6">
                        {t('checklist-items')}
                    </Typography>

                    {items.map((item, index) => (
                        <Box
                            key={index}
                            border="1px solid"
                            borderColor="divider"
                            borderRadius={1}
                            p={3}
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t('item')} {index + 1}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => removeItem(index)}
                                    disabled={items.length === 1}
                                    color="error"
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>

                            <TextField
                                label={t('question') + ' *'}
                                value={item.label}
                                onChange={e => updateItem(index, { label: e.target.value })}
                                fullWidth
                            />

                            <FormControl fullWidth>
                                <InputLabel>{t('answer-type')}</InputLabel>
                                <Select
                                    value={item.answerType}
                                    label={t('answer-type')}
                                    onChange={e =>
                                        updateItem(index, { answerType: e.target.value as ChecklistAnswerType, options: [] })
                                    }
                                >
                                    <MenuItem value={ChecklistAnswerType.Boolean}>{answerTypeLabel(ChecklistAnswerType.Boolean)}</MenuItem>
                                    <MenuItem value={ChecklistAnswerType.Text}>{answerTypeLabel(ChecklistAnswerType.Text)}</MenuItem>
                                    <MenuItem value={ChecklistAnswerType.Radio}>{answerTypeLabel(ChecklistAnswerType.Radio)}</MenuItem>
                                </Select>
                            </FormControl>

                            {item.answerType === ChecklistAnswerType.Radio && (
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('options')}
                                    </Typography>

                                    {item.options.map((option, optIndex) => (
                                        <Box key={optIndex} display="flex" alignItems="center" gap={1} pl={1}>
                                            <Typography variant="body2" flex={1}>
                                                {option}
                                            </Typography>
                                            <IconButton size="small" onClick={() => removeOption(index, optIndex)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}

                                    <Box display="flex" gap={1} mt={1}>
                                        <TextField
                                            size="small"
                                            label={t('new-option')}
                                            value={item.newOption}
                                            onChange={e => updateItem(index, { newOption: e.target.value })}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    addOption(index)
                                                }
                                            }}
                                            fullWidth
                                        />
                                        <Button variant="outlined" onClick={() => addOption(index)} disabled={!item.newOption.trim()}>
                                            {t('add', { ns: 'common' })}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ))}

                    <Button startIcon={<Add />} variant="outlined" onClick={addItem} sx={{ alignSelf: 'flex-start' }}>
                        {t('add-item')}
                    </Button>

                    <Divider />

                    <Box display="flex" gap={2} justifyContent="flex-end">
                        <CancelButton onClick={() => navigate(AppRoutesNavigation.Customer.checklists(customerId!))} />
                        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
                            {isEdit ? t('save', { ns: 'common' }) : t('create', { ns: 'common' })}
                        </Button>
                    </Box>

                </Box>
            </Box>
        </CommonPageLayout>
    )
}

export default ChecklistForm